import type { TFunction } from 'i18next';
import type { ShowcaseBlock } from '../types';
import { GRADIENT } from '../tokens';

interface Ctx {
  t: TFunction;
  prefix: string;
}

export function ShowcaseRenderer({ blocks, ctx }: { blocks: ShowcaseBlock[]; ctx: Ctx }) {
  return (
    <div className="space-y-24">
      {blocks.map((b, i) => (
        <Block key={i} block={b} ctx={ctx} />
      ))}
    </div>
  );
}

// A single showcase block placed before the section heading (no space-y wrapper).
export function StandaloneBlock({ block, ctx }: { block: ShowcaseBlock; ctx: Ctx }) {
  return <Block block={block} ctx={ctx} />;
}

function Block({ block, ctx }: { block: ShowcaseBlock; ctx: Ctx }) {
  const { t, prefix } = ctx;
  const g = (key: string) => GRADIENT[key];

  switch (block.kind) {
    case 'banner':
      return (
        <div className="mb-24 relative group">
          <div className={`absolute -inset-1 bg-gradient-to-r ${g(block.gradient)} rounded-3xl blur opacity-20 group-hover:opacity-40 transition duration-1000 group-hover:duration-200`} />
          <div className="relative glass-card rounded-3xl p-2 border border-white/10 overflow-hidden">
            <div className="w-full bg-dark-200/50 rounded-2xl flex items-center justify-center text-text-muted overflow-hidden">
              <img src={prefix + block.src} alt={t(block.altKey)} className="object-cover w-full h-auto max-h-[70vh]" />
            </div>
            {block.overlayTitleKey && (
              <div className="absolute bottom-6 left-6 right-6 lg:bottom-10 lg:left-10 text-center lg:text-left glass-card p-6 rounded-2xl border border-white/10 backdrop-blur-md max-w-lg">
                <h3 className="text-2xl font-bold text-text-primary font-heading mb-2">{t(block.overlayTitleKey)}</h3>
                {block.overlayDescKey && <p className="text-text-secondary">{t(block.overlayDescKey)}</p>}
              </div>
            )}
          </div>
        </div>
      );

    case 'row': {
      const fit = block.imgFit === 'contain' ? 'object-contain' : 'object-cover';
      return (
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          <div className={`flex-1 space-y-6 ${block.reverse ? 'lg:order-2' : ''}`}>
            <h3 className="text-3xl font-bold text-text-primary font-heading">{t(block.titleKey)}</h3>
            <p className="text-lg text-text-secondary leading-relaxed">{t(block.descKey)}</p>
          </div>
          <div className={`flex-[1.5] relative group ${block.reverse ? 'lg:order-1' : ''}`}>
            <div className={`absolute -inset-1 bg-gradient-to-r ${g(block.gradient)} rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-1000 group-hover:duration-200`} />
            <div className="relative glass-card rounded-2xl p-2 border border-white/10 overflow-hidden">
              <div className="w-full bg-dark-200/50 rounded-xl flex items-center justify-center overflow-hidden">
                <img src={prefix + block.image.src} alt={t(block.image.altKey)} className={`${fit} w-full h-full`} />
              </div>
            </div>
          </div>
        </div>
      );
    }

    case 'pair':
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
          {block.columns.map((col, ci) => {
            const contain = col.imgFit === 'contain';
            return (
              <div key={ci} className="space-y-6 relative group">
                <h3 className="text-2xl font-bold text-text-primary font-heading">{t(col.titleKey)}</h3>
                <p className="text-text-secondary leading-relaxed">{t(col.descKey)}</p>
                <div className={`absolute -inset-1 bg-gradient-to-r ${g(col.gradient)} rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-1000 group-hover:duration-200 -z-10`} />
                {col.images.map((img, ii) => (
                  <div
                    key={ii}
                    className={`relative glass-card rounded-2xl p-2 border border-white/10 overflow-hidden z-10 w-full ${ii === 0 ? 'mt-6' : 'mt-4'}${contain ? '' : ` ${col.aspect ?? 'aspect-[4/3]'}`}`}
                  >
                    {contain ? (
                      <div className="w-full h-full bg-dark-200/50 rounded-xl flex items-center justify-center py-4 px-4 min-h-[300px]">
                        <img src={prefix + img.src} alt={t(img.altKey)} className="max-h-[350px] w-full object-contain rounded-lg shadow-lg" />
                      </div>
                    ) : (
                      <div className="w-full h-full bg-dark-200/50 rounded-xl flex items-center justify-center overflow-hidden">
                        <img src={prefix + img.src} alt={t(img.altKey)} className="object-cover w-full h-full object-top" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      );

    case 'collage':
      return (
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          <div className="flex-1 space-y-6">
            {block.headings.map((h, hi) => (
              <div key={hi} className={hi === 0 ? 'mb-10' : ''}>
                <h3 className="text-3xl font-bold text-text-primary font-heading mb-4">{t(h.titleKey)}</h3>
                <p className="text-lg text-text-secondary leading-relaxed">{t(h.descKey)}</p>
              </div>
            ))}
          </div>
          <div className="flex-[1.5] relative group">
            <div className={`absolute -inset-1 bg-gradient-to-r ${g(block.gradient)} rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-1000 group-hover:duration-200`} />
            <div className={`relative glass-card rounded-2xl p-4 border border-white/10 overflow-hidden ${block.gap ?? 'gap-4'} flex flex-col bg-dark-200/50`}>
              <img src={prefix + block.hero.src} alt={t(block.hero.altKey)} className="object-cover w-full h-auto rounded-xl shadow-md border border-white/5" />
              {block.rows.map((row, ri) => (
                <div key={ri} className="grid grid-cols-2 gap-4">
                  {row.map((img, ii) => (
                    <img key={ii} src={prefix + img.src} alt={t(img.altKey)} className="object-cover w-full h-auto rounded-xl shadow-md border border-white/5" />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      );

    case 'gallery': {
      const colCls = block.cols === 2 ? 'grid-cols-2' : block.cols === 3 ? 'grid-cols-2 md:grid-cols-3' : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4';
      const grad = GRADIENT[block.gradient ?? 'purple-cyan'];
      return (
        <div className={`grid ${colCls} gap-5 sm:gap-6`}>
          {block.images.map((img, i) => (
            <div key={i} className="relative group">
              <div className={`absolute -inset-1 bg-gradient-to-r ${grad} rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-1000 group-hover:duration-200`} />
              <div className="relative glass-card rounded-2xl p-2 border border-white/10 overflow-hidden bg-dark-200/50 flex flex-col">
                <div className="w-full rounded-xl overflow-hidden bg-dark/50">
                  <img src={prefix + img.src} alt={t(img.altKey)} loading="lazy" className="object-cover object-top w-full h-full group-hover:scale-105 transition-transform duration-700" />
                </div>
                {img.labelKey && (
                  <div className="px-2 pt-3 pb-2 text-center mt-auto">
                    <span className="text-sm font-medium text-text-primary">{t(img.labelKey)}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      );
    }

    case 'video': {
      return (
        <div className="relative group">
          <div className="absolute -inset-1 bg-gradient-to-r from-neon-purple to-neon-cyan rounded-3xl blur opacity-20 group-hover:opacity-40 transition duration-1000 group-hover:duration-200" />
          <div className={`relative glass-card border border-white/10 overflow-hidden ${block.rounded === false ? '' : 'rounded-3xl p-2'}`}>
            <video
              src={prefix + block.src}
              poster={block.poster ? prefix + block.poster : undefined}
              autoPlay
              loop
              muted
              playsInline
              className={`w-full h-auto ${block.rounded === false ? '' : 'rounded-2xl'}`}
            />
          </div>
        </div>
      );
    }

    case 'modules':
      return (
        <div className="space-y-32">
          {block.modules.map((mod, index) => {
            const isEven = index % 2 === 0;
            return (
              <div key={mod.key} className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-start gap-10 lg:gap-16`}>
                <div className="flex-1 space-y-6 lg:sticky lg:top-32">
                  <h3 className="text-3xl font-bold text-text-primary font-heading">{t(`modules.${mod.key}.title`)}</h3>
                  <p className="text-lg text-text-secondary leading-relaxed">{t(`modules.${mod.key}.desc`)}</p>
                </div>
                <div className="flex-[1.5] w-full">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {mod.images.map((img, imgIdx) => {
                      const isFullWidth = (mod.images.length === 3 && imgIdx === 2) || mod.images.length === 1;
                      const label = t(`modules.labels.${img.labelKey}`);
                      return (
                        <div key={imgIdx} className={`relative group ${isFullWidth ? 'sm:col-span-2' : ''}`}>
                          <div className="absolute -inset-1 bg-gradient-to-r from-neon-purple to-neon-cyan rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-1000 group-hover:duration-200" />
                          <div className="relative glass-card rounded-2xl p-2 border border-white/10 overflow-hidden flex flex-col h-full bg-dark-200/50">
                            <div className={`w-full ${isFullWidth ? 'aspect-[21/9]' : 'aspect-[16/11]'} rounded-xl overflow-hidden mb-3 bg-dark/50`}>
                              <img src={prefix + img.src} alt={label} className="object-cover object-top w-full h-full hover:scale-105 transition-transform duration-700" />
                            </div>
                            <div className="px-2 pb-2 text-center mt-auto">
                              <span className="text-sm font-medium text-text-primary">{label}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
  }
}
